'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

interface Stream {
  title: string;
  brand: string;
  gmv: string;
  viewers: string;
  duration: string;
  date: string;
  accent: string;
  img: number;
}

interface Review {
  brand: string;
  text: string;
  rating: number;
  date: string;
  img: number;
}

const streams: Stream[] = [
  { title: 'GlowCo Summer Skincare Haul', brand: 'GlowCo Beauty', gmv: '$84K', viewers: '42K', duration: '2h 15m', date: 'Jul 28', accent: 'bg-theme-01', img: 10 },
  { title: 'VitaBoost Wellness Bundle Drop', brand: 'VitaBoost Labs', gmv: '$61K', viewers: '31K', duration: '1h 50m', date: 'Jul 14', accent: 'bg-theme-03', img: 14 },
  { title: 'NestWell Home Refresh Event', brand: 'NestWell', gmv: '$47K', viewers: '24K', duration: '2h 05m', date: 'Jun 30', accent: 'bg-theme-04', img: 18 },
  { title: 'GlowCo Holiday Collection', brand: 'GlowCo Beauty', gmv: '$112K', viewers: '58K', duration: '3h 00m', date: 'Jun 12', accent: 'bg-theme-01', img: 10 },
];

const reviews: Review[] = [
  { brand: 'GlowCo Beauty', text: 'Aria is exceptional — she drove $112K GMV in a single 3-hour stream. Her ability to connect with the audience and explain product benefits is unmatched.', rating: 5, date: 'Aug 2024', img: 10 },
  { brand: 'VitaBoost Labs', text: 'Professional, punctual, and incredibly effective. Our conversion rate during her streams was 3x our average. Will definitely rebook.', rating: 5, date: 'Jul 2024', img: 14 },
  { brand: 'NestWell', text: 'Great communicator and very knowledgeable about our product line. Viewers loved her energy and authenticity.', rating: 4, date: 'Jun 2024', img: 18 },
];

const calendarDays = Array.from({ length: 31 }, (_, i) => i + 1);
const bookedDays = [3, 7, 8, 14, 15, 21, 22, 28];
const availableDays = [1, 2, 4, 5, 6, 9, 10, 11, 12, 13, 16, 17, 18, 19, 20, 23, 24, 25, 26, 27, 29, 30, 31];

export default function SellerProfilePage() {
  const [activeTab, setActiveTab] = useState<'streams' | 'reviews' | 'calendar'>('streams');
  const [showBooking, setShowBooking] = useState(false);

  return (
    <main className="font-montserrat bg-white text-dark overflow-x-hidden">
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-0 bg-neutral-01">
        <div className="max-w-[1200px] mx-auto px-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-[13px] text-dark/40 mb-8">
            <Link href="/" className="hover:text-dark transition-colors">Home</Link>
            <span>/</span>
            <span className="text-dark/60">Sellers</span>
            <span>/</span>
            <span className="text-dark">Aria Chen</span>
          </div>

          {/* Profile Header */}
          <div className="flex flex-col md:flex-row md:items-end gap-8 pb-10">
            <div className="relative flex-shrink-0">
              <img
                src="https://i.pravatar.cc/120?img=30"
                alt="Aria Chen TikTok live seller profile photo"
                className="w-32 h-32 rounded-[28px] object-cover shadow-lg"
              />
              <div className="absolute -bottom-2 -right-2 w-5 h-5 bg-theme-green rounded-full border-2 border-white" />
            </div>

            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <h1 className="text-[36px] md:text-[44px] font-bold text-dark leading-tight">Aria Chen</h1>
                <span className="bg-theme-01 text-dark text-[12px] font-bold px-3 py-1 rounded-full">Beauty & Skincare</span>
                <span className="bg-theme-green/15 text-theme-green text-[12px] font-bold px-3 py-1 rounded-full">Available</span>
              </div>
              <p className="text-[16px] text-dark/60 max-w-[600px] leading-relaxed mb-5">
                Top-rated TikTok Shop live seller specialising in beauty and skincare. 2.4M followers, consistent $300K+ monthly GMV, and a 5.0 star rating across 142 brand partnerships.
              </p>
              <div className="flex flex-wrap gap-6">
                {[
                  { label: 'GMV/month', value: '$320K' },
                  { label: 'Followers', value: '2.4M' },
                  { label: 'Rating', value: '⭐ 5.0' },
                  { label: 'Streams Done', value: '84' },
                  { label: 'Avg. Viewers', value: '38K' },
                ].map((stat) => (
                  <div key={stat.label}>
                    <p className="text-[22px] font-bold text-dark">{stat.value}</p>
                    <p className="text-[12px] text-dark/50">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-3 flex-shrink-0">
              <button
                onClick={() => setShowBooking(true)}
                className="font-bold text-[14px] text-dark bg-theme-04 px-7 py-3.5 rounded-pill hover:brightness-95 transition-all duration-200 text-center"
              >
                Book Aria Now
              </button>
              <button className="font-bold text-[14px] text-dark border border-dark/20 px-7 py-3.5 rounded-pill hover:bg-neutral-02 transition-all duration-200">
                Send Message
              </button>
            </div>
          </div>

          {/* Skills Bar */}
          <div className="bg-white rounded-t-[28px] px-8 py-5 flex flex-wrap gap-6 border-b border-neutral-02">
            {[
              { icon: '🎙️', label: 'Languages', value: 'English, Mandarin' },
              { icon: '📦', label: 'Categories', value: 'Skincare, Makeup, K-Beauty' },
              { icon: '⏱️', label: 'Avg. Stream', value: '2h 15m' },
              { icon: '💰', label: 'Rate', value: 'From $2,500/stream' },
              { icon: '📍', label: 'Based in', value: 'Los Angeles, CA' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2.5">
                <span className="text-[18px]">{item.icon}</span>
                <div>
                  <p className="text-[11px] text-dark/40 font-medium">{item.label}</p>
                  <p className="text-[13px] font-bold text-dark">{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="bg-white sticky top-[72px] z-40 border-b border-neutral-02">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex gap-0">
            {(['streams', 'reviews', 'calendar'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`font-bold text-[14px] px-6 py-4 border-b-2 transition-all duration-200 capitalize ${
                  activeTab === tab
                    ? 'border-dark text-dark' :'border-transparent text-dark/40 hover:text-dark/70'
                }`}
              >
                {tab === 'streams' ? 'Past Live Streams' : tab === 'reviews' ? 'Reviews' : 'Availability'}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <section className="py-14 bg-white">
        <div className="max-w-[1200px] mx-auto px-6">

          {/* Past Streams */}
          {activeTab === 'streams' && (
            <div>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-[28px] font-bold text-dark">Past Live Streams</h2>
                <span className="text-[13px] text-dark/40 font-medium">84 streams total</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {streams.map((s, i) => (
                  <div key={i} className="bg-neutral-01 rounded-[24px] p-6 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-start gap-4 mb-5">
                      <div className={`w-12 h-12 ${s.accent} rounded-2xl flex items-center justify-center flex-shrink-0 overflow-hidden`}>
                        <img src={`https://i.pravatar.cc/48?img=${s.img}`} alt={`${s.brand} brand logo for stream`} className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <h3 className="font-bold text-dark text-[15px] leading-snug">{s.title}</h3>
                        <p className="text-[12px] text-dark/50 mt-0.5">{s.brand} · {s.date}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { label: 'GMV', value: s.gmv },
                        { label: 'Peak Viewers', value: s.viewers },
                        { label: 'Duration', value: s.duration },
                      ].map((m) => (
                        <div key={m.label} className="bg-white rounded-xl p-3 text-center">
                          <p className="text-[11px] text-dark/40 mb-0.5">{m.label}</p>
                          <p className="text-[14px] font-bold text-dark">{m.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reviews */}
          {activeTab === 'reviews' && (
            <div>
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h2 className="text-[28px] font-bold text-dark">Brand Reviews</h2>
                  <p className="text-[14px] text-dark/50 mt-1">142 reviews · 5.0 average</p>
                </div>
                <div className="flex items-center gap-2 bg-neutral-01 px-5 py-3 rounded-2xl">
                  <span className="text-[28px] font-bold text-dark">5.0</span>
                  <div>
                    <div className="flex gap-0.5">
                      {[1,2,3,4,5].map(s => <span key={s} className="text-theme-04 text-[16px]">★</span>)}
                    </div>
                    <p className="text-[11px] text-dark/40">142 reviews</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-5">
                {reviews.map((r, i) => (
                  <div key={i} className="bg-neutral-01 rounded-[24px] p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <img src={`https://i.pravatar.cc/48?img=${r.img}`} alt={`${r.brand} reviewer logo`} className="w-12 h-12 rounded-2xl object-cover flex-shrink-0" />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <p className="font-bold text-dark text-[15px]">{r.brand}</p>
                          <span className="text-[12px] text-dark/40">{r.date}</span>
                        </div>
                        <div className="flex gap-0.5 mt-1">
                          {Array.from({ length: r.rating }).map((_, s) => (
                            <span key={s} className="text-theme-04 text-[14px]">★</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-[15px] text-dark/70 leading-relaxed">{r.text}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Availability Calendar */}
          {activeTab === 'calendar' && (
            <div>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-[28px] font-bold text-dark">Availability — August 2024</h2>
                <button
                  onClick={() => setShowBooking(true)}
                  className="font-bold text-[14px] text-dark bg-theme-04 px-6 py-3 rounded-pill hover:brightness-95 transition-all duration-200"
                >
                  Request Booking
                </button>
              </div>
              <div className="bg-neutral-01 rounded-[28px] p-8">
                <div className="flex gap-5 mb-6">
                  {[
                    { color: 'bg-theme-green', label: 'Available' },
                    { color: 'bg-neutral-02', label: 'Booked' },
                  ].map((l) => (
                    <div key={l.label} className="flex items-center gap-2">
                      <div className={`w-3 h-3 ${l.color} rounded-full`} />
                      <span className="text-[13px] text-dark/60 font-medium">{l.label}</span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-2 mb-3">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                    <div key={d} className="text-center text-[12px] font-bold text-dark/40 py-2">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {/* Offset for August 2024 starting on Thursday */}
                  {[0,1,2,3].map((_, i) => <div key={`empty-${i}`} />)}
                  {calendarDays.map((day) => {
                    const isBooked = bookedDays.includes(day);
                    const isAvailable = availableDays.includes(day);
                    return (
                      <button
                        key={day}
                        onClick={() => isAvailable && setShowBooking(true)}
                        className={`aspect-square rounded-xl flex items-center justify-center text-[14px] font-bold transition-all duration-200 ${
                          isBooked
                            ? 'bg-neutral-02 text-dark/30 cursor-not-allowed' :'bg-theme-green/20 text-dark hover:bg-theme-green hover:text-white cursor-pointer'
                        }`}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Booking Modal */}
      {showBooking && (
        <div className="fixed inset-0 bg-dark/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[28px] p-8 w-full max-w-[480px] shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-[22px] font-bold text-dark">Book Aria Chen</h3>
              <button onClick={() => setShowBooking(false)} className="w-8 h-8 bg-neutral-01 rounded-full flex items-center justify-center hover:bg-neutral-02 transition-colors">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M18 6L6 18M6 6l12 12" stroke="#120A0B" strokeWidth="2.5" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <div className="flex flex-col gap-4">
              <div>
                <label className="text-[13px] font-bold text-dark/60 mb-1.5 block">Your Brand Name</label>
                <input type="text" placeholder="e.g. GlowCo Beauty" className="w-full bg-neutral-01 rounded-xl px-4 py-3 text-[14px] font-medium text-dark outline-none focus:ring-2 focus:ring-theme-01 transition-all" />
              </div>
              <div>
                <label className="text-[13px] font-bold text-dark/60 mb-1.5 block">Preferred Date</label>
                <input type="date" className="w-full bg-neutral-01 rounded-xl px-4 py-3 text-[14px] font-medium text-dark outline-none focus:ring-2 focus:ring-theme-01 transition-all" />
              </div>
              <div>
                <label className="text-[13px] font-bold text-dark/60 mb-1.5 block">Stream Duration</label>
                <select className="w-full bg-neutral-01 rounded-xl px-4 py-3 text-[14px] font-medium text-dark outline-none focus:ring-2 focus:ring-theme-01 transition-all">
                  <option>1 hour</option>
                  <option>2 hours</option>
                  <option>3 hours</option>
                  <option>Custom</option>
                </select>
              </div>
              <div>
                <label className="text-[13px] font-bold text-dark/60 mb-1.5 block">Message (optional)</label>
                <textarea rows={3} placeholder="Tell Aria about your brand and products..." className="w-full bg-neutral-01 rounded-xl px-4 py-3 text-[14px] font-medium text-dark outline-none focus:ring-2 focus:ring-theme-01 transition-all resize-none" />
              </div>
              <button className="w-full font-bold text-[15px] text-dark bg-theme-04 py-4 rounded-pill hover:brightness-95 transition-all duration-200 mt-2">
                Send Booking Request
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </main>
  );
}
